import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckIcon,
  ChevronLeftIcon,
  CloseIcon,
  MicrophoneFilledIcon,
  WarningIcon,
} from "@/components/Icons";
import AndroidDictationDemoVideo from "@/components/AndroidDictationDemoVideo";
import AndroidHandleDemo from "@/components/AndroidHandleDemo";
import {
  configureGlobalDictationAsync,
  getGlobalDictationStatusAsync,
  GlobalDictationStatus,
  isGlobalDictationAvailable,
  openGlobalDictationAccessibilitySettingsAsync,
  openGlobalDictationOverlaySettingsAsync,
  requestGlobalDictationMicrophonePermissionAsync,
  setGlobalDictationEnabledAsync,
} from "@/models/useGlobalDictation";
import GlobalDictationOnboardingIOS from "@/components/GlobalDictationOnboardingIOS";
import GlobalDictationOnboardingDesktop from "@/components/GlobalDictationOnboardingDesktop";
import { desktop } from "@/controller";
import { getPlatform, getPlatformRuntime } from "@/platform";
import { config } from "@/controller";
import { useAppStore } from "@/controller";
import { logClientEvent } from "@/telemetry";
import {
  androidDictationSteps,
  canAcceptAccessibilityDisclosure,
  getAndroidDictationOpeningStep,
  getAndroidSettingsFlavor,
  getAndroidPrimaryEffect,
  getNextDictationStep,
  getPreviousDictationStep,
  getVisibleAndroidDictationSteps,
  type AndroidSettingsFlavor,
  type AndroidDictationStep,
  type DesktopDictationStep,
} from "@/dictation-interactions";

type Props = {
  open: boolean;
  initialStep?: AndroidDictationStep;
  desktopInitialStep?: DesktopDictationStep;
  onDesktopComplete?: () => void;
  onClose: () => void;
};

type SettingsPermission = "overlay" | "accessibility";
type SettingsHandoffState =
  | "idle"
  | "opening"
  | "returned-off"
  | "granted"
  | "launch-error";

type SettingsHandoff = {
  permission?: SettingsPermission;
  state: SettingsHandoffState;
  attempt: number;
};

const SETTINGS_SUCCESS_MS = 500;
const idleSettingsHandoff: SettingsHandoff = { state: "idle", attempt: 0 };

const GlobalDictationOnboardingAndroid = ({
  open,
  initialStep,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const userToken = useAppStore((state) => state.userToken);
  const setOnboardingDismissed = useAppStore(
    (state) => state.setGlobalDictationOnboardingDismissed,
  );
  const setAccessibilityDisclosureAccepted = useAppStore(
    (state) => state.setGlobalDictationAccessibilityDisclosureAccepted,
  );
  const completeIntro = useAppStore(
    (state) => state.completeGlobalDictationIntro,
  );

  const resolveOpeningStep = useCallback(
    () =>
      getAndroidDictationOpeningStep(
        Boolean(useAppStore.getState().globalDictationIntroCompleted),
        initialStep || "intro",
      ),
    [initialStep],
  );

  const [stepKey, setStepKey] =
    useState<AndroidDictationStep>(resolveOpeningStep);
  const [status, setStatus] = useState<GlobalDictationStatus>();
  const [busy, setBusy] = useState(false);
  const [accessibilityDisclosureOpen, setAccessibilityDisclosureOpen] =
    useState(false);
  const [
    accessibilityDisclosureConsentChecked,
    setAccessibilityDisclosureConsentChecked,
  ] = useState(false);
  const [accessibilityDisclosureBusy, setAccessibilityDisclosureBusy] =
    useState(false);
  const [settingsFlavor, setSettingsFlavor] =
    useState<AndroidSettingsFlavor>("generic");
  const [settingsHandoff, setSettingsHandoff] =
    useState<SettingsHandoff>(idleSettingsHandoff);
  const [settingsHelpOpen, setSettingsHelpOpen] = useState(false);
  const [completionFailed, setCompletionFailed] = useState(false);
  const stepKeyRef = useRef(stepKey);
  stepKeyRef.current = stepKey;
  const settingsHandoffRef = useRef<
    | {
        permission: SettingsPermission;
        attempt: number;
        openedAt: number;
      }
    | undefined
  >(undefined);
  const accessibilityAcceptedThisSessionRef = useRef(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const syncAccessibilityDisclosureConsent = useCallback(
    (next: GlobalDictationStatus) => {
      if (
        !next.accessibilityGranted &&
        useAppStore.getState().globalDictationAccessibilityDisclosureAccepted
      ) {
        setAccessibilityDisclosureAccepted(false);
      }
    },
    [setAccessibilityDisclosureAccepted],
  );

  const refreshStatus = useCallback(async () => {
    if (!isGlobalDictationAvailable()) return;
    try {
      const next = await configureGlobalDictationAsync({
        authToken: userToken || "",
        serverUrl: config.apiUrl,
      });
      setStatus(next);
      syncAccessibilityDisclosureConsent(next);
      return next;
    } catch (error) {
      console.error("[GlobalDictation] configure failed", error);
      const fallback = await getGlobalDictationStatusAsync();
      setStatus(fallback);
      syncAccessibilityDisclosureConsent(fallback);
      return fallback;
    }
  }, [userToken, syncAccessibilityDisclosureConsent]);

  useEffect(() => {
    if (!open) {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = undefined;
      }
      return;
    }
    setStepKey(resolveOpeningStep());
    setAccessibilityDisclosureOpen(false);
    setAccessibilityDisclosureConsentChecked(false);
    setSettingsHandoff(idleSettingsHandoff);
    setSettingsHelpOpen(false);
    setCompletionFailed(false);
    settingsHandoffRef.current = undefined;
    accessibilityAcceptedThisSessionRef.current = false;
  }, [open, resolveOpeningStep]);

  useEffect(() => {
    if (!open) return;
    getPlatformRuntime()
      .device.getInfo()
      .then((info) =>
        setSettingsFlavor(getAndroidSettingsFlavor(info.manufacturer)),
      )
      .catch(() => setSettingsFlavor("generic"));
  }, [open]);

  useEffect(
    () => () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    },
    [],
  );

  // One semantic event per step transition while open. Completion is emitted
  // only after native confirms that Dictation is enabled.
  useEffect(() => {
    if (!open) return;
    logClientEvent("dictation.onboardingStepViewed", {
      step: stepKey,
    });
  }, [open, stepKey]);

  // Re-run configure whenever the token changes so the native side stays in
  // sync — but don't reset the step.
  useEffect(() => {
    if (!open) return;
    refreshStatus();
  }, [open, refreshStatus]);

  // Re-check status when the user comes back from system settings.
  // If the active step's permission just got granted, advance automatically.
  useEffect(() => {
    if (!open || !isGlobalDictationAvailable()) return;
    let listener: { remove: () => Promise<void> } | undefined;
    getPlatformRuntime()
      .app.onResume(async () => {
        const next = await refreshStatus();
        if (!next) return;
        const active = stepKeyRef.current;
        const handoff = settingsHandoffRef.current;
        if (handoff && handoff.permission === active) {
          const granted =
            handoff.permission === "overlay"
              ? next.overlayGranted
              : next.accessibilityGranted;
          settingsHandoffRef.current = undefined;
          logClientEvent("dictation.settingsReturned", {
            permission: handoff.permission,
            granted,
            attempt: handoff.attempt,
            elapsedMs: Date.now() - handoff.openedAt,
            settingsFlavor,
          });
          setSettingsHandoff({
            permission: handoff.permission,
            state: granted ? "granted" : "returned-off",
            attempt: handoff.attempt,
          });
          if (!granted) return;
          logClientEvent("dictation.permissionResult", {
            permission: handoff.permission,
            granted: true,
          });
          successTimerRef.current = setTimeout(() => {
            successTimerRef.current = undefined;
            advance(handoff.permission);
          }, SETTINGS_SUCCESS_MS);
          return;
        }
        if (active === "microphone" && next.microphoneGranted)
          advance("microphone");
        else if (active === "overlay" && next.overlayGranted) {
          logClientEvent("dictation.permissionResult", {
            permission: "overlay",
            granted: true,
          });
          advance("overlay");
        } else if (active === "accessibility" && next.accessibilityGranted) {
          logClientEvent("dictation.permissionResult", {
            permission: "accessibility",
            granted: true,
          });
          advance("accessibility");
        }
      })
      .then((handle) => {
        listener = handle;
      });
    return () => {
      listener?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshStatus, settingsFlavor]);

  // Android system back button always dismisses the onboarding.
  useEffect(() => {
    if (!open || !isGlobalDictationAvailable()) return;
    let listener: { remove: () => Promise<void> } | undefined;
    getPlatformRuntime()
      .app.onBackButton(() => {
        if (accessibilityDisclosureOpen) {
          setAccessibilityDisclosureOpen(false);
          setAccessibilityDisclosureConsentChecked(false);
          return;
        }
        onClose();
      })
      .then((handle) => {
        listener = handle;
      });
    return () => {
      listener?.remove();
    };
  }, [open, onClose, accessibilityDisclosureOpen]);

  // Skip permission steps that are already granted — no point asking the user
  // to "Continue" through something they've already done.
  const visibleSteps = useMemo(() => {
    return getVisibleAndroidDictationSteps(status);
  }, [status]);

  const stepIndex = visibleSteps.indexOf(stepKey);

  // If status loads (or changes via the resume listener) and the current step
  // just got skipped, jump forward to the next visible step.
  useEffect(() => {
    if (!open || visibleSteps.length === 0) return;
    if (visibleSteps.includes(stepKey)) return;
    if (
      settingsHandoff.permission === stepKey &&
      settingsHandoff.state === "granted"
    ) {
      return;
    }
    const cursor = androidDictationSteps.indexOf(stepKey);
    const next =
      visibleSteps.find((s) => androidDictationSteps.indexOf(s) > cursor) ||
      visibleSteps[visibleSteps.length - 1];
    setStepKey(next);
  }, [open, settingsHandoff, stepKey, visibleSteps]);

  const advance = (from?: AndroidDictationStep) => {
    setStepKey((current) => {
      const base = from ?? current;
      return getNextDictationStep(visibleSteps, base);
    });
  };

  const goBack = () => {
    setStepKey((current) => getPreviousDictationStep(visibleSteps, current));
  };

  const openSettings = async (permission: SettingsPermission) => {
    const attempt =
      settingsHandoff.permission === permission
        ? settingsHandoff.attempt + 1
        : 1;
    setSettingsHelpOpen(false);
    setSettingsHandoff({ permission, state: "opening", attempt });
    settingsHandoffRef.current = {
      permission,
      attempt,
      openedAt: Date.now(),
    };
    logClientEvent("dictation.settingsOpened", {
      permission,
      attempt,
      settingsFlavor,
    });
    try {
      if (permission === "overlay") {
        await openGlobalDictationOverlaySettingsAsync();
      } else {
        await openGlobalDictationAccessibilitySettingsAsync();
      }
    } catch (error) {
      console.error(`[GlobalDictation] ${permission} settings failed`, error);
      settingsHandoffRef.current = undefined;
      setSettingsHandoff({ permission, state: "launch-error", attempt });
      logClientEvent("dictation.settingsReturned", {
        permission,
        granted: false,
        attempt,
        settingsFlavor,
        status: "launch-error",
      });
    }
  };

  const finishAndClose = async () => {
    setCompletionFailed(false);
    try {
      const next = await setGlobalDictationEnabledAsync(true);
      setStatus(next);
      if (!next.setupComplete) {
        setCompletionFailed(true);
        return;
      }
    } catch (error) {
      console.error("[GlobalDictation] enable failed", error);
      setCompletionFailed(true);
      return;
    }
    setOnboardingDismissed(true);
    logClientEvent("dictation.onboardingCompleted");
    onClose();
  };

  const requestAccessibilityDisclosure = () => {
    setAccessibilityDisclosureConsentChecked(false);
    setAccessibilityDisclosureOpen(true);
    logClientEvent("dictation.accessibilityDisclosureViewed");
  };

  const declineAccessibilityDisclosure = () => {
    setAccessibilityDisclosureOpen(false);
    setAccessibilityDisclosureConsentChecked(false);
    logClientEvent("dictation.accessibilityDisclosureDeclined");
  };

  const acceptAccessibilityDisclosure = async () => {
    if (
      !canAcceptAccessibilityDisclosure({
        checked: accessibilityDisclosureConsentChecked,
        busy: accessibilityDisclosureBusy,
      })
    ) {
      return;
    }

    setAccessibilityDisclosureBusy(true);
    try {
      accessibilityAcceptedThisSessionRef.current = true;
      setAccessibilityDisclosureAccepted(true);
      setAccessibilityDisclosureOpen(false);
      logClientEvent("dictation.accessibilityDisclosureAccepted");
      await openSettings("accessibility");
    } finally {
      setAccessibilityDisclosureBusy(false);
    }
  };

  const handlePrimary = async () => {
    if (
      busy ||
      (settingsHandoff.permission === stepKey &&
        settingsHandoff.state === "opening")
    ) {
      return;
    }
    setBusy(true);
    try {
      switch (getAndroidPrimaryEffect(stepKey, status)) {
        case "advance":
          if (stepKey === "intro") completeIntro();
          advance();
          break;
        case "requestMicrophone": {
          const next = await requestGlobalDictationMicrophonePermissionAsync();
          setStatus(next);
          logClientEvent("dictation.permissionResult", {
            permission: "microphone",
            granted: Boolean(next.microphoneGranted),
          });
          if (next.microphoneGranted) advance("microphone");
          break;
        }
        case "openOverlaySettings":
          await openSettings("overlay");
          break;
        case "openAccessibilityDisclosure":
          if (accessibilityAcceptedThisSessionRef.current) {
            await openSettings("accessibility");
          } else {
            requestAccessibilityDisclosure();
          }
          break;
        case "complete":
          await finishAndClose();
          break;
      }
    } finally {
      setBusy(false);
    }
  };

  const isPermissionStep =
    stepKey === "microphone" ||
    stepKey === "overlay" ||
    stepKey === "accessibility";
  const activeHandoff =
    settingsHandoff.permission === stepKey
      ? settingsHandoff
      : idleSettingsHandoff;
  const showSettingsRecovery =
    activeHandoff.state === "returned-off" ||
    activeHandoff.state === "launch-error";
  const deferSettings = () => {
    logClientEvent("dictation.onboardingSkipped", {
      step: stepKey,
      reason: "settings-deferred",
      attempt: activeHandoff.attempt,
    });
    onClose();
  };

  const primaryLabel = useMemo(() => {
    const continueLabel = t("globalDictationOnboarding.cta.continue", {
      defaultValue: "Continue",
    });
    switch (stepKey) {
      case "intro":
        return t("globalDictationOnboarding.cta.getStarted", {
          defaultValue: "Set up the bubble",
        });
      case "microphone":
        return status?.microphoneGranted
          ? continueLabel
          : t("globalDictationOnboarding.cta.allow", { defaultValue: "Allow" });
      case "overlay":
        if (status?.overlayGranted) return continueLabel;
        if (activeHandoff.state === "returned-off") {
          return t("globalDictationOnboarding.cta.openSettingsAgain", {
            defaultValue: "Open settings again",
          });
        }
        if (activeHandoff.state === "launch-error") {
          return t("globalDictationOnboarding.cta.retry", {
            defaultValue: "Retry",
          });
        }
        return t("globalDictationOnboarding.cta.openDisplaySettings", {
          defaultValue: "Open display settings",
        });
      case "accessibility":
        if (status?.accessibilityGranted) return continueLabel;
        if (activeHandoff.state === "returned-off") {
          return t("globalDictationOnboarding.cta.openSettingsAgain", {
            defaultValue: "Open settings again",
          });
        }
        if (activeHandoff.state === "launch-error") {
          return t("globalDictationOnboarding.cta.retry", {
            defaultValue: "Retry",
          });
        }
        return t("globalDictationOnboarding.cta.reviewAccessibility", {
          defaultValue: "Review accessibility access",
        });
      case "done":
        return t("globalDictationOnboarding.cta.turnOn", {
          defaultValue: "Turn on dictation",
        });
    }
  }, [activeHandoff.state, stepKey, t, status]);

  if (!open) return null;
  if (!isGlobalDictationAvailable()) return null;

  return (
    <div className="app-page-background fixed inset-0 z-[60] flex flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] animate-in fade-in duration-200">
      {/* Background ambience */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[15%] -left-[10%] h-[45%] w-[55%] rounded-full bg-[var(--ambience-blue)] opacity-50 blur-[80px]" />
        <div className="absolute -bottom-[10%] -right-[10%] h-[40%] w-[50%] rounded-full bg-[var(--ambience-purple)] opacity-50 blur-[70px]" />
      </div>

      <div
        className="relative flex h-full flex-col"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Top bar: progress + back */}
        <div className="flex items-center gap-3 px-5 pt-4">
          <button
            data-action-id="dictation.setup.backOrClose"
            onClick={stepIndex === 0 ? onClose : goBack}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full glass-tile active:bg-[var(--glass-bg-active)] active:scale-95 transition-transform"
            aria-label={
              stepIndex === 0
                ? t("globalDictationOnboarding.close", {
                    defaultValue: "Close",
                  })
                : t("globalDictationOnboarding.back", { defaultValue: "Back" })
            }
          >
            {stepIndex === 0 ? (
              <CloseIcon size={18} />
            ) : (
              <ChevronLeftIcon size={20} />
            )}
          </button>

          <div className="flex flex-1 items-center gap-1">
            {visibleSteps.map((key, idx) => {
              const filled = idx <= stepIndex;
              return (
                <span
                  key={key}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    filled
                      ? "bg-[var(--accent)]"
                      : "bg-[var(--glass-bg-active)]"
                  }`}
                />
              );
            })}
          </div>

          <span className="w-10" />
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-8">
          <div
            key={stepKey}
            className="h-full animate-in fade-in slide-in-from-bottom-2 duration-200 motion-reduce:animate-none"
          >
            {stepKey === "intro" && <IntroStep t={t} />}
            {stepKey === "microphone" && (
              <MicrophoneStep
                t={t}
                granted={Boolean(status?.microphoneGranted)}
              />
            )}
            {stepKey === "overlay" && (
              <OverlayStep
                t={t}
                granted={Boolean(status?.overlayGranted)}
                flavor={settingsFlavor}
                handoff={activeHandoff}
                helpOpen={settingsHelpOpen}
                onHelp={() => setSettingsHelpOpen((current) => !current)}
              />
            )}
            {stepKey === "accessibility" && (
              <AccessibilityStep
                t={t}
                granted={Boolean(status?.accessibilityGranted)}
                flavor={settingsFlavor}
                handoff={activeHandoff}
                helpOpen={settingsHelpOpen}
                onHelp={() => setSettingsHelpOpen((current) => !current)}
              />
            )}
            {stepKey === "done" && (
              <DoneStep t={t} completionFailed={completionFailed} />
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="px-5 pb-8 pt-3">
          <button
            data-action-id="dictation.setup.continue"
            disabled={
              busy ||
              activeHandoff.state === "opening" ||
              activeHandoff.state === "granted"
            }
            onClick={handlePrimary}
            aria-busy={busy || activeHandoff.state === "opening"}
            className="h-14 w-full rounded-full bg-[var(--accent)] text-[var(--text-inverse)] font-semibold shadow-lg shadow-[var(--accent-glow)] active:scale-[0.99] transition-transform disabled:opacity-60"
          >
            {busy ? "…" : primaryLabel}
          </button>
          {showSettingsRecovery && (
            <button
              type="button"
              data-action-id="dictation.setup.defer"
              onClick={deferSettings}
              className="mt-2 h-10 w-full rounded-full text-sm font-medium text-[var(--text-secondary)] active:text-[var(--text-primary)]"
            >
              {t("globalDictationOnboarding.cta.notNow", {
                defaultValue: "Not now",
              })}
            </button>
          )}
          {isPermissionStep && (
            <p className="mt-3 text-center text-xs text-[var(--text-tertiary)]">
              {t("globalDictationOnboarding.permissionRequired", {
                defaultValue:
                  "This permission is required for Dictation to work.",
              })}
            </p>
          )}
        </div>
      </div>

      <AccessibilityDisclosureSheet
        t={t}
        open={accessibilityDisclosureOpen}
        checked={accessibilityDisclosureConsentChecked}
        busy={accessibilityDisclosureBusy}
        onCheckedChange={setAccessibilityDisclosureConsentChecked}
        onCancel={declineAccessibilityDisclosure}
        onContinue={acceptAccessibilityDisclosure}
      />
    </div>
  );
};

type StepProps = { t: ReturnType<typeof useTranslation>["t"] };

type AccessibilityDisclosureSheetProps = StepProps & {
  open: boolean;
  checked: boolean;
  busy: boolean;
  onCheckedChange: (checked: boolean) => void;
  onCancel: () => void;
  onContinue: () => void;
};

const AccessibilityDisclosureSheet = ({
  t,
  open,
  checked,
  busy,
  onCheckedChange,
  onCancel,
  onContinue,
}: AccessibilityDisclosureSheetProps) => {
  if (!open) return null;

  const disclosureItems = [
    t("globalDictationOnboarding.accessibilityDisclosure.itemActiveField", {
      defaultValue:
        "Detect whether recording started from an editable text field.",
    }),
    t("globalDictationOnboarding.accessibilityDisclosure.itemFieldText", {
      defaultValue:
        "Return your finished dictation to that field when it is still available. Password fields are avoided.",
    }),
    t("globalDictationOnboarding.accessibilityDisclosure.itemInsertion", {
      defaultValue:
        "Show a review-and-copy sheet when text cannot be inserted.",
    }),
    t("globalDictationOnboarding.accessibilityDisclosure.itemTranscription", {
      defaultValue:
        "Send your recorded audio and dictation request to 1Transcribe for transcription. General screen contents are not collected.",
    }),
  ];

  return (
    <div className="scrim absolute inset-0 z-20 flex items-center justify-center px-4 py-4 animate-in fade-in duration-200">
      <button
        data-action-id="dictation.accessibility.cancel"
        type="button"
        className="absolute inset-0"
        aria-label={t(
          "globalDictationOnboarding.accessibilityDisclosure.cancel",
          {
            defaultValue: "Cancel",
          },
        )}
        onClick={onCancel}
      />
      <section
        role="dialog"
        aria-modal="true"
        className="relative flex max-h-[75dvh] w-full flex-col overflow-hidden rounded-[28px] bg-[var(--bg-primary)] px-5 pb-5 pt-4 text-[var(--text-primary)] shadow-2xl animate-in zoom-in-95 duration-200"
      >
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-[var(--glass-bg-active)]" />
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">
            {t("globalDictationOnboarding.accessibilityDisclosure.eyebrow", {
              defaultValue: "Required disclosure",
            })}
          </p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight">
            {t("globalDictationOnboarding.accessibilityDisclosure.title", {
              defaultValue: "Allow accessibility access for Dictation",
            })}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
            {t("globalDictationOnboarding.accessibilityDisclosure.subtitle", {
              defaultValue:
                "1Transcribe uses Android Accessibility permission only to return dictation to text fields in other apps.",
            })}
          </p>

          <ul className="mt-4 space-y-3 pb-4">
            {disclosureItems.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
                </span>
                <span className="text-[var(--text-secondary)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="shrink-0 border-t border-[var(--hairline)] bg-[var(--bg-primary)] pt-4">
          <button
            data-action-id="dictation.accessibility.consent.toggle"
            type="button"
            role="checkbox"
            aria-checked={checked}
            onClick={() => onCheckedChange(!checked)}
            className="flex w-full items-start gap-3 rounded-2xl glass-tile p-4 text-start active:bg-[var(--glass-bg-active)]"
          >
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
                checked
                  ? "border-[var(--accent)] bg-[var(--accent)]"
                  : "border-[var(--text-muted)]"
              }`}
            >
              {checked && (
                <CheckIcon
                  size={14}
                  strokeWidth={3}
                  className="text-[var(--text-inverse)]"
                />
              )}
            </span>
            <span className="text-sm font-medium leading-relaxed">
              {t("globalDictationOnboarding.accessibilityDisclosure.consent", {
                defaultValue:
                  "I understand and agree to this use of Android Accessibility permission.",
              })}
            </span>
          </button>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              data-action-id="dictation.accessibility.cancel"
              type="button"
              disabled={busy}
              onClick={onCancel}
              className="h-12 rounded-full glass-tile text-sm font-semibold text-[var(--text-secondary)] active:bg-[var(--glass-bg-active)] disabled:opacity-60"
            >
              {t("globalDictationOnboarding.accessibilityDisclosure.cancel", {
                defaultValue: "Cancel",
              })}
            </button>
            <button
              data-action-id="dictation.accessibility.continue"
              type="button"
              disabled={busy || !checked}
              onClick={onContinue}
              className="h-12 rounded-full bg-[var(--accent)] text-sm font-semibold text-[var(--text-inverse)] shadow-lg shadow-[var(--accent-glow)] active:scale-[0.99] transition-transform disabled:opacity-50"
            >
              {busy
                ? "..."
                : t("globalDictationOnboarding.cta.openSettings", {
                    defaultValue: "Open settings",
                  })}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const IntroStep = ({ t }: StepProps) => (
  <div className="flex h-full flex-col items-center text-center">
    <h1 className="max-w-[320px] text-3xl font-semibold leading-tight">
      {t("globalDictationOnboarding.intro.title", {
        defaultValue: "Stop typing. Just talk.",
      })}
    </h1>
    <p className="mt-3 max-w-[320px] text-sm leading-relaxed text-[var(--text-secondary)]">
      {t("globalDictationOnboarding.intro.subtitle", {
        defaultValue:
          "Tap the handle on the edge of your screen, speak, then tap ✓.",
      })}
    </p>

    <AndroidDictationDemoVideo className="mt-5" />

    <p className="mt-4 max-w-[320px] text-xs leading-relaxed text-[var(--text-secondary)]">
      {t("globalDictationOnboarding.intro.behavior", {
        defaultValue:
          "Start from a text field to insert automatically. Otherwise, your words are copied so you can paste them.",
      })}
    </p>
  </div>
);

const MicrophoneStep = ({ t, granted }: StepProps & { granted: boolean }) => (
  <div className="flex h-full flex-col items-center text-center">
    <div className="relative mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-[var(--accent)]/15">
      <MicrophoneFilledIcon size={42} className="text-[var(--accent)]" />
      {granted && (
        <span className="absolute -right-1 -bottom-1 flex h-8 w-8 items-center justify-center rounded-full bg-[var(--accent)] shadow-lg shadow-[var(--accent-glow)]">
          <CheckIcon
            size={16}
            className="text-[var(--text-inverse)]"
            strokeWidth={3}
          />
        </span>
      )}
    </div>
    <h2 className="text-2xl font-semibold">
      {t("globalDictationOnboarding.microphone.title", {
        defaultValue: "Allow microphone",
      })}
    </h2>
    <p className="mt-3 max-w-[320px] text-sm leading-relaxed text-[var(--text-secondary)]">
      {t("globalDictationOnboarding.microphone.subtitle", {
        defaultValue: "1Transcribe listens only while the bubble is recording.",
      })}
    </p>
    {granted && (
      <p className="mt-4 text-sm font-medium text-[var(--accent)]">
        {t("globalDictationOnboarding.granted", {
          defaultValue: "Permission granted",
        })}
      </p>
    )}
  </div>
);

type SettingsStepProps = StepProps & {
  granted: boolean;
  flavor: AndroidSettingsFlavor;
  handoff: SettingsHandoff;
  helpOpen: boolean;
  onHelp: () => void;
};

const OverlayStep = ({
  t,
  granted,
  flavor,
  handoff,
  helpOpen,
  onHelp,
}: SettingsStepProps) => (
  <div className="flex h-full flex-col items-center text-center">
    <AndroidHandleDemo className="max-w-[200px]" />
    <h2 className="mt-5 text-2xl font-semibold">
      {t("globalDictationOnboarding.overlay.title", {
        defaultValue: "Keep the handle on your screen",
      })}
    </h2>
    <p className="mt-3 max-w-[340px] text-sm leading-relaxed text-[var(--text-secondary)]">
      {t("globalDictationOnboarding.overlay.subtitle", {
        defaultValue:
          "Android needs one setting so the handle can stay at the edge of your screen while you type.",
      })}
    </p>

    <OverlaySettingsGuide t={t} flavor={flavor} className="mt-6" />
    <SettingsHandoffFeedback
      t={t}
      permission="overlay"
      granted={granted}
      handoff={handoff}
      helpOpen={helpOpen}
      onHelp={onHelp}
    />
  </div>
);

const AccessibilityStep = ({
  t,
  granted,
  flavor,
  handoff,
  helpOpen,
  onHelp,
}: SettingsStepProps) => (
  <div className="flex h-full flex-col items-center text-center">
    <div className="inline-flex items-center gap-1.5 rounded-full bg-[var(--success)]/10 px-3 py-1.5 text-xs font-semibold text-[var(--success)]">
      <CheckIcon size={14} strokeWidth={2.5} />
      {t("globalDictationOnboarding.settingsCoach.oneSettingLeft", {
        defaultValue: "Bubble ready · One setting left",
      })}
    </div>
    <h2 className="mt-5 text-2xl font-semibold">
      {t("globalDictationOnboarding.accessibility.title", {
        defaultValue: "Let 1Transcribe return your words",
      })}
    </h2>
    <p className="mt-3 max-w-[340px] text-sm leading-relaxed text-[var(--text-secondary)]">
      {t("globalDictationOnboarding.accessibility.subtitle", {
        defaultValue:
          "Android calls this Accessibility access. 1Transcribe uses it to return your finished dictation to the text field where you started. Password fields are skipped.",
      })}
    </p>

    <AccessibilitySettingsGuide t={t} flavor={flavor} className="mt-6" />
    <SettingsHandoffFeedback
      t={t}
      permission="accessibility"
      granted={granted}
      handoff={handoff}
      helpOpen={helpOpen}
      onHelp={onHelp}
    />
  </div>
);

const OverlaySettingsGuide = ({
  t,
  flavor,
  className = "",
}: StepProps & {
  flavor: AndroidSettingsFlavor;
  className?: string;
}) => {
  const toggleLabel =
    flavor === "samsung"
      ? t("globalDictationOnboarding.settingsCoach.allowPermission", {
          defaultValue: "Allow permission",
        })
      : t("globalDictationOnboarding.overlay.toggleLabel", {
          defaultValue: "Display over other apps",
        });
  const spokenInstruction =
    flavor === "samsung"
      ? t(
          "globalDictationOnboarding.settingsCoach.visualOverlayDirectSamsung",
          {
            defaultValue:
              "Settings opens directly to 1Transcribe. Turn on Allow permission.",
          },
        )
      : t(
          "globalDictationOnboarding.settingsCoach.visualOverlayDirectGeneric",
          {
            defaultValue:
              "Settings opens directly to 1Transcribe. Turn on Display over other apps.",
          },
        );

  return (
    <div
      role="img"
      aria-label={spokenInstruction}
      className={`w-full max-w-[340px] rounded-2xl glass-tile p-3 text-start ${className}`}
    >
      <div className="px-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
          {t("globalDictationOnboarding.settingsCoach.next", {
            defaultValue: "Next in Settings",
          })}
        </p>
        <p className="mt-1 text-xs text-[var(--text-secondary)]">
          {t("globalDictationOnboarding.settingsCoach.directOpen", {
            defaultValue: "Opens directly to this switch",
          })}
        </p>
      </div>
      <div
        aria-hidden="true"
        className="mt-3 flex min-h-16 items-center gap-3 rounded-xl bg-[var(--selected-bg)] px-3 py-2.5"
      >
        <img src="/logo.png" alt="" className="h-8 w-8 shrink-0 rounded-lg" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[var(--text-primary)]">
            1Transcribe
          </span>
          <span className="mt-0.5 block text-xs leading-tight text-[var(--text-secondary)]">
            {toggleLabel}
          </span>
        </span>
        <span className="overlay-setting-switch flex h-6 w-11 shrink-0 items-center rounded-full p-0.5">
          <span className="overlay-setting-switch-knob h-5 w-5 rounded-full bg-white shadow" />
        </span>
      </div>
    </div>
  );
};

const AccessibilitySettingsGuide = ({
  t,
  flavor,
  className = "",
}: StepProps & {
  flavor: AndroidSettingsFlavor;
  className?: string;
}) => {
  const route =
    flavor === "samsung"
      ? [
          t("globalDictationOnboarding.accessibility.installedApps", {
            defaultValue: "Installed apps",
          }),
          "1Transcribe",
          t("globalDictationOnboarding.cta.allow", {
            defaultValue: "Allow",
          }),
        ]
      : [
          t("globalDictationOnboarding.settingsCoach.findOneTranscribe", {
            defaultValue: "Find 1Transcribe",
          }),
          t("globalDictationOnboarding.settingsCoach.useOneTranscribe", {
            defaultValue: "Use 1Transcribe",
          }),
          t("globalDictationOnboarding.cta.allow", {
            defaultValue: "Allow",
          }),
        ];

  const spokenInstruction =
    flavor === "samsung"
      ? t(
          "globalDictationOnboarding.settingsCoach.visualAccessibilitySamsung",
          {
            defaultValue:
              "In Accessibility Settings, open Installed apps, choose 1Transcribe, then allow access.",
          },
        )
      : t(
          "globalDictationOnboarding.settingsCoach.visualAccessibilityGeneric",
          {
            defaultValue:
              "In Accessibility Settings, find 1Transcribe, choose Use 1Transcribe, then allow access.",
          },
        );

  return (
    <div
      role="img"
      aria-label={spokenInstruction}
      className={`w-full max-w-[340px] rounded-2xl glass-tile p-3 text-start ${className}`}
    >
      <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
        {t("globalDictationOnboarding.settingsCoach.next", {
          defaultValue: "Next in Settings",
        })}
      </p>
      <div aria-hidden="true" className="relative mt-2">
        <span className="absolute bottom-6 start-6 top-6 w-px bg-[var(--hairline)]" />
        <ol className="space-y-1">
          {route.map((label, index) => (
            <li
              key={`${index}-${label}`}
              className="settings-coach-step relative flex min-h-11 items-center gap-3 rounded-xl px-2.5 py-2"
              style={{ animationDelay: `${index * 0.65}s` }}
            >
              <span className="relative z-[1] flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--bg-primary)] text-xs font-bold text-[var(--accent)] shadow-[var(--glass-edge-soft)]">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 text-sm font-semibold leading-tight text-[var(--text-primary)]">
                {label}
              </span>
              {index === 1 && (
                <img
                  src="/logo.png"
                  alt=""
                  className="h-7 w-7 shrink-0 rounded-lg"
                />
              )}
              {index === route.length - 1 && (
                <span className="flex h-6 w-11 shrink-0 items-center justify-end rounded-full bg-[var(--accent)] p-0.5 shadow-inner">
                  <span className="h-5 w-5 rounded-full bg-[var(--text-inverse)] shadow" />
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

const SettingsHandoffFeedback = ({
  t,
  permission,
  granted,
  handoff,
  helpOpen,
  onHelp,
}: StepProps & {
  permission: SettingsPermission;
  granted: boolean;
  handoff: SettingsHandoff;
  helpOpen: boolean;
  onHelp: () => void;
}) => {
  if (granted || handoff.state === "granted") {
    return (
      <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-[var(--success)] animate-in fade-in duration-200">
        <CheckIcon size={17} strokeWidth={2.5} />
        {t("globalDictationOnboarding.granted", {
          defaultValue: "Permission granted",
        })}
      </p>
    );
  }

  if (handoff.state !== "returned-off" && handoff.state !== "launch-error") {
    return null;
  }

  const launchFailed = handoff.state === "launch-error";
  return (
    <div
      role="status"
      className="mt-4 w-full max-w-[340px] rounded-2xl bg-[var(--warning)]/10 px-4 py-3 text-start"
    >
      <div className="flex items-start gap-2.5">
        <WarningIcon
          size={18}
          className="mt-0.5 shrink-0 text-[var(--warning)]"
        />
        <div>
          <p className="text-sm font-semibold">
            {launchFailed
              ? t("globalDictationOnboarding.settingsCoach.launchErrorTitle", {
                  defaultValue: "Settings didn't open",
                })
              : t("globalDictationOnboarding.settingsCoach.stillOffTitle", {
                  defaultValue: "The setting is still off",
                })}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
            {launchFailed
              ? t("globalDictationOnboarding.settingsCoach.launchErrorBody", {
                  defaultValue: "Try opening Android Settings again.",
                })
              : t("globalDictationOnboarding.settingsCoach.stillOffBody", {
                  defaultValue:
                    "Turn on the last switch for 1Transcribe, then come back.",
                })}
          </p>
        </div>
      </div>

      {!launchFailed && handoff.attempt >= 2 && (
        <div className="mt-2.5 border-t border-[var(--hairline)] pt-2.5">
          <button
            type="button"
            data-action-id="dictation.setup.help"
            aria-expanded={helpOpen}
            onClick={onHelp}
            className="text-xs font-semibold text-[var(--accent)]"
          >
            {t("globalDictationOnboarding.settingsCoach.help", {
              defaultValue: "Can't turn it on?",
            })}
          </button>
          {helpOpen && (
            <p className="mt-2 text-xs leading-relaxed text-[var(--text-secondary)]">
              {permission === "overlay"
                ? t("globalDictationOnboarding.settingsCoach.helpOverlay", {
                    defaultValue:
                      "Search Android Settings for 1Transcribe, then enable Appear on top or Display over other apps.",
                  })
                : t(
                    "globalDictationOnboarding.settingsCoach.helpAccessibility",
                    {
                      defaultValue:
                        "Search Accessibility for 1Transcribe, open it under Installed apps, then choose Allow.",
                    },
                  )}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const DoneStep = ({
  t,
  completionFailed,
}: StepProps & { completionFailed: boolean }) => (
  <div className="flex h-full flex-col items-center justify-center text-center">
    <AndroidHandleDemo className="max-w-[220px]" />
    <h2 className="mt-7 max-w-[320px] text-3xl font-semibold leading-tight">
      {t("globalDictationOnboarding.done.title", {
        defaultValue: "You're all set",
      })}
    </h2>
    <p className="mt-4 max-w-[320px] text-sm leading-relaxed text-[var(--text-secondary)]">
      {t("globalDictationOnboarding.done.subtitle", {
        defaultValue: "Look for the slim handle on the edge of your screen.",
      })}
    </p>
    {/* The handle is small on purpose; teach its three gestures once. */}
    <ul
      data-testid="android-handle-tips"
      className="mt-5 w-full max-w-[300px] space-y-2 text-start text-sm"
    >
      {(
        [
          ["tapTip", "Tap it to dictate"],
          ["holdTip", "Hold it for language, meetings, and hide"],
          ["dragTip", "Drag it to move it"],
        ] as const
      ).map(([key, fallback]) => (
        <li
          key={key}
          className="flex items-center gap-3 rounded-2xl glass-tile px-4 py-3"
        >
          <span className="desktop-lesson-bubble shrink-0" data-mode="idle" />
          <span className="text-[var(--text-primary)]">
            {t(`globalDictationOnboarding.done.${key}`, {
              defaultValue: fallback,
            })}
          </span>
        </li>
      ))}
    </ul>
    {completionFailed && (
      <p
        role="status"
        className="mt-5 flex max-w-[320px] items-center gap-2 rounded-2xl bg-[var(--warning)]/10 px-4 py-3 text-start text-sm text-[var(--text-secondary)]"
      >
        <WarningIcon size={18} className="shrink-0 text-[var(--warning)]" />
        {t("globalDictationOnboarding.done.enableFailed", {
          defaultValue: "Dictation couldn't be turned on. Try again.",
        })}
      </p>
    )}
  </div>
);

// Dictation has three capability platforms. Electron deliberately remains the
// shared web runtime everywhere else; only this setup dispatcher treats it as
// desktop so the iOS and Android execution paths stay unchanged.
const GlobalDictationOnboarding = (props: Props) =>
  desktop.isAvailable ? (
    <GlobalDictationOnboardingDesktop
      open={props.open}
      initialStep={props.desktopInitialStep}
      onClose={props.onClose}
      onComplete={props.onDesktopComplete ?? props.onClose}
    />
  ) : getPlatform() === "ios" ? (
    <GlobalDictationOnboardingIOS open={props.open} onClose={props.onClose} />
  ) : (
    <GlobalDictationOnboardingAndroid {...props} />
  );

export default GlobalDictationOnboarding;
